import { useState } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { getMarkerMoveDistanceMiles } from 'features/geoEvents/utils/markerMoveDistance';
import { LatLng } from 'shared/types';
import { INCIDENT_MARKER_MOVE_CONFIRM_THRESHOLD_MILES } from '../../../../constants';

export type UseMarkerMoveConfirmationProps = {
  originalLatLng: LatLng | null;
  currentLatLng: LatLng | null;
  isEditing: boolean;
};

type UseMarkerMoveConfirmationResult = {
  checkbox: JSX.Element | null;
  isBlockingSubmit: boolean;
};

// Returns the checkbox JSX so the form doesn't have to wire up the threshold check, label, and confirmed state itself.
// It's a slightly unusual pattern, but it avoids a lot of boilerplate code in the forms that need this.
export const useMarkerMoveConfirmation = (
  props: UseMarkerMoveConfirmationProps,
): UseMarkerMoveConfirmationResult => {
  const { originalLatLng, currentLatLng, isEditing } = props;
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);

  if (!isEditing) {
    return { checkbox: null, isBlockingSubmit: false };
  }

  const moveDistanceMiles = getMarkerMoveDistanceMiles(
    originalLatLng,
    currentLatLng,
  );

  if (moveDistanceMiles <= INCIDENT_MARKER_MOVE_CONFIRM_THRESHOLD_MILES) {
    return { checkbox: null, isBlockingSubmit: false };
  }

  return {
    checkbox: (
      <>
        <Typography variant="body1" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('editGeoEvent.confirmationHeader')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
          }
          label={
            <Trans
              i18nKey="editGeoEvent.confirmMarkerMove"
              values={{ miles: moveDistanceMiles.toFixed(1) }}
              components={[<strong key="miles" />]}
            />
          }
        />
      </>
    ),
    isBlockingSubmit: !confirmed,
  };
};
